import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { useContext } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext } from '../../../../../context';
import type { InstrumentType } from '../../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { TranscriptionDialogContext } from '../../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { JobType } from '../../../../../entities';
import { INSTRUMENTS } from './Instrument.config';
import InstrumentsDetectionWrapper from './InstrumentsDetectionWrapper';
import {
  TrackingCategory,
  trackingService,
  TrackingTranscriptionParams,
} from '../../../../../services';

const useStyles = makeStyles()((theme) => ({
  instrumentSelection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '&:hover': {
      boxShadow: `0px 0px 5px 0 ${theme.palette.secondary.main}`,
    },
    '&:disabled': {
      filter: 'grayscale(.8) opacity(0.8)',
    },
  },
  instruments: {
    gap: 8,
    margin: 5,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      gap: 2,
    },
  },
  label: {
    height: '4em',
    width: '6em',
    color: '#d1cfcf',
    fontSize: '12px',
    textTransform: 'initial',
  },
  selected: {
    boxShadow: `0px 0px 5px 0 ${theme.palette.primary.main}`,
  },
  checkbox: {
    width: '100%',
    textAlign: 'center',
  },
  buttons: {
    textAlign: 'right',
  },
}));

export interface InstrumentsSelectionProps {
  handleMultipleSelection: (instruments: InstrumentType[]) => void;
  isRetranscribe?: boolean;
}

const InstrumentsSelection: React.FC<InstrumentsSelectionProps> = ({
  handleMultipleSelection,
  isRetranscribe,
}) => {
  const { classes } = useStyles();
  const { jobType } = useContext(AppConstantsContext);
  const { setSettings } = useContext(TranscriptionDialogContext);
  const { t } = useTranslation();
  const [instruments, setInstruments] = React.useState<InstrumentType[]>([]);
  const [showNextButton, setShowNextButton] = React.useState(false);
  const [disableInstruments, setDisableInstruments] = React.useState(false);

  const handleClick = (type: InstrumentType | 'multiple') => {
    if (type === 'multiple') {
      window.open('https://klang.io/transcription-studio', '_blank');
      return;
    }
    if (instruments.includes(type)) {
      const filteredInstruments = instruments.filter((instrument) => instrument !== type);
      setShowNextButton(filteredInstruments.length > 0);
      setInstruments(filteredInstruments);
    } else if (jobType === JobType.TranscribeStudio) {
      setInstruments([...instruments, type]);
      setShowNextButton(true);
    } else {
      setSettings({ instruments: [type] });
      handleMultipleSelection(instruments);
    }
  };

  const handleClickNext = () => {
    const trackingValues = {
      instruments,
      amount: instruments.length,
      automatic: disableInstruments,
    };
    trackingService.valueObjectEvent<{ instruments: string[]; amount: number; automatic: boolean }>(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.instruments,
      { ...trackingValues },
    );
    console.log(TrackingTranscriptionParams.action.instruments, trackingValues);
    setSettings({ instruments });
    handleMultipleSelection(instruments);
  };

  const handleRadioChange = (detection: boolean) => {
    if (detection) {
      setDisableInstruments(true);
      setInstruments([]);
      setShowNextButton(true);
    } else {
      setDisableInstruments(false);
      setShowNextButton(false);
    }
  };

  return (
    <>
      <InstrumentsDetectionWrapper
        handleRadioChange={handleRadioChange}
        showRadios={jobType === JobType.TranscribeStudio}
      >
        <div className={classes.instruments}>
          {INSTRUMENTS?.[jobType]?.config.map((iconConfig) =>
            (!iconConfig.component ? (
              <div key={iconConfig.type} className={classes.checkbox}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      name={iconConfig.type}
                      key={iconConfig.type}
                      onClick={() => handleClick(iconConfig.type)}
                      id={`btn_wizard_${iconConfig.type}`}
                      checked={instruments.includes(iconConfig.type as InstrumentType)}
                    />
                  )}
                  label={t(iconConfig.label)}
                />
              </div>
            ) : (
              !(iconConfig.type === 'multiple' && isRetranscribe) && (
                <Button
                  name={iconConfig.type}
                  disabled={disableInstruments}
                  id={`btn_wizard_${iconConfig.type}`}
                  className={
                    instruments.includes(iconConfig.type as InstrumentType)
                      ? `${classes.instrumentSelection} ${classes.selected}`
                      : classes.instrumentSelection
                  }
                  key={iconConfig.label}
                  onClick={() => handleClick(iconConfig.type)}
                >
                  <div>{iconConfig.component}</div>
                  <div className={classes.label}>{t(iconConfig.label)}</div>
                </Button>
              )
            )))}
        </div>
      </InstrumentsDetectionWrapper>
      {showNextButton && jobType === JobType.TranscribeStudio && (
        <div className={classes.buttons}>
          <Button
            id="btn_wizard_choose_instrument_next"
            onClick={handleClickNext}
            variant="contained"
          >
            {t('home.input.dialog.next')}
          </Button>
        </div>
      )}
    </>
  );
};

export default InstrumentsSelection;
